function getTimestamp(value) {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getConversationSortTime(conversation) {
  return (
    getTimestamp(conversation?.last_activity_at) ||
    getTimestamp(conversation?.created_at) ||
    0
  );
}

function getConversationContactId(conversation) {
  return (
    conversation?.meta?.sender?.id ||
    conversation?.contact_id ||
    conversation?.contact?.id ||
    null
  );
}

function normalizeEmail(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizePhoneNumber(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^\d+]/g, '');
}

function getConversationContactGroupingKey(conversation) {
  const contactId = getConversationContactId(conversation);
  if (contactId) return `contact:${contactId}`;

  const sender = conversation?.meta?.sender || conversation?.contact || {};
  const email = normalizeEmail(sender.email);
  if (email) return `email:${email}`;

  const phoneNumber = normalizePhoneNumber(sender.phone_number || sender.phoneNumber);
  if (phoneNumber) return `phone:${phoneNumber}`;

  const identifier =
    typeof sender.identifier === 'string' ? sender.identifier.trim() : '';
  if (identifier) return `identifier:${identifier}`;

  return `conversation:${conversation?.id}`;
}

/**
 * Groups conversations by contact (sender) so multiple channels (eg. Email + Telegram)
 * appear as a single list item.
 *
 * The returned items keep the shape of a Conversation, but include:
 * - grouped_conversations_count
 * - grouped_conversation_ids
 *
 * It also aggregates unread_count.
 */
export function groupConversationsByContact(conversations = []) {
  if (!Array.isArray(conversations) || conversations.length === 0) return [];

  const groups = new Map();

  conversations.forEach(conversation => {
    const key = getConversationContactGroupingKey(conversation);

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key).push(conversation);
  });

  const grouped = [];

  groups.forEach(groupConversations => {
    const sorted = [...groupConversations].sort(
      (a, b) => getConversationSortTime(b) - getConversationSortTime(a)
    );

    const primary = sorted[0];
    const groupedConversationIds = sorted
      .map(c => c?.id)
      .filter(id => id !== null && id !== undefined);

    const unreadCount = sorted.reduce(
      (sum, c) => sum + (Number(c?.unread_count) || 0),
      0
    );

    grouped.push({
      ...primary,
      unread_count: unreadCount,
      grouped_conversations_count: groupedConversationIds.length,
      grouped_conversation_ids: groupedConversationIds,
      grouped_conversations: sorted,
    });
  });

  return grouped.sort(
    (a, b) => getConversationSortTime(b) - getConversationSortTime(a)
  );
}

export default groupConversationsByContact;
